import React from 'react';

interface getEventInterceptorResult {
    handleMouseDown: (event: MouseEvent | React.MouseEvent<HTMLElement>) => void;
    handleMouseUp: (event: MouseEvent | React.MouseEvent<HTMLElement>) => void;
    handleKeyDown: (event: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => void;
    addDocumentListeners: () => void;
    removeDocumentListeners: () => void;
}

export const getEventInterceptors = (
    targetRef: React.MutableRefObject<HTMLElement>,
    callback?: (event: MouseEvent | React.MouseEvent<HTMLElement>) => void,
): getEventInterceptorResult => {
    const handleKeyDown = (event: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
            callback?.call(null, event);
        }
    };

    const handleMouseDown = (event: MouseEvent | React.MouseEvent<HTMLElement>) => {
        if (!targetRef.current) {
            return;
        }

        if (targetRef.current && !targetRef.current.contains(event.target as Node)) {
            callback?.call(null, event);
            return;
        }

        event.stopPropagation();
    };

    const handleMouseUp = (event: MouseEvent | React.MouseEvent<HTMLElement>) => {
        if (targetRef.current && !targetRef.current.contains(event.target as Node)) {
            callback?.call(null, event);
            return;
        }

        if (!targetRef.current) {
            return;
        }

        event.stopPropagation();
    };

    /*
        If you need to detect these events and handle them outside the target element, use the
        add and remove calls below:

        const { addDocumentListeners, removeDocumentListeners } = getEventInterceptors(<targetRef>, [<callback>]);

        useEffect(() => {
            addDocumentListeners();

            return () => {
                removeDocumentListeners();
            };
        }, []);

        If you listen on these events on your target element as well, be sure to stop propagation in
        the handler if you are not using handler returned here.
     */
    const addDocumentListeners = () => {
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const removeDocumentListeners = () => {
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('keydown', handleKeyDown);
    };

    return {
        handleMouseDown,
        handleMouseUp,
        handleKeyDown,
        addDocumentListeners,
        removeDocumentListeners,
    };
};
